import { useIntl } from 'react-intl';
import { z } from 'zod';

import { getChannelDefaults } from '@/components/pages/job-posting/channel-defaults';
import {
  MIN_CHANNEL_BUDGET,
  ZIPRECRUITER_BUDGET_PRESETS,
} from '@/components/pages/job-posting/validation/constants';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { ZIPRECRUITER } from '@/types/channels';

const ZIPRECRUITER_BUDGET_VALUES = new Set<number>(
  ZIPRECRUITER_BUDGET_PRESETS.map(p => p.value),
);

/**
 * Hook that provides channel-aware budget and duration field schemas
 * aligned with `getChannelDefaults`.
 *
 * Shared by the general recommendation form and the budget recommendation form.
 */
export function useChannelFieldSchemas() {
  const intl = useIntl();
  const { formatCurrency } = useCurrencySymbol();

  const messages = {
    budgetRequired: intl.formatMessage({
      defaultMessage: 'Budget is required',
      description: 'Budget input required error message',
    }),
    durationRequired: intl.formatMessage({
      defaultMessage: 'Duration is required',
      description: 'Duration input required error message',
    }),
    invalidZipRecruiterBudget: intl.formatMessage({
      defaultMessage: 'Select a valid budget',
      description: 'ZipRecruiter budget must match a preset option',
    }),
    durationMustBeWholeDays: intl.formatMessage({
      defaultMessage: 'Enter a whole number of days',
      description: 'Duration must be an integer',
    }),
    minimumBudget: (amount: number) =>
      intl.formatMessage(
        {
          defaultMessage: 'Minimum budget is {amount}',
          description: 'Budget below channel minimum',
        },
        { amount: formatCurrency(amount) },
      ),
    fixedDuration: (days: number) =>
      intl.formatMessage(
        {
          defaultMessage: 'Duration must be {days} days',
          description: 'Fixed campaign duration for channel',
        },
        { days },
      ),
    minimumDuration: (days: number) =>
      intl.formatMessage(
        {
          defaultMessage: 'Minimum duration is {days} days',
          description: 'Minimum campaign duration for channel',
        },
        { days },
      ),
  };

  /**
   * Budget field schema for a channel:
   * - ZipRecruiter: must match a preset in `ZIPRECRUITER_BUDGET_PRESETS`.
   * - Other channels: must be at least `minBudget` (channel-specific) or `MIN_CHANNEL_BUDGET`.
   */
  const getBudgetSchema = (channelSlug: string) => {
    if (channelSlug === ZIPRECRUITER) {
      return z
        .number({ message: messages.budgetRequired })
        .refine((v: number) => ZIPRECRUITER_BUDGET_VALUES.has(v), {
          message: messages.invalidZipRecruiterBudget,
        });
    }

    const minBudget =
      getChannelDefaults(channelSlug).minBudget ?? MIN_CHANNEL_BUDGET;

    return z
      .number({ message: messages.budgetRequired })
      .min(minBudget, { message: messages.minimumBudget(minBudget) });
  };

  /**
   * Duration field schema for a channel:
   * - Channels with a fixed `duration` (e.g. ZipRecruiter, Boost): must equal that duration.
   * - Other channels: whole number of days, at least `minDuration` (or 1).
   */
  const getDurationSchema = (channelSlug: string) => {
    const defaults = getChannelDefaults(channelSlug);

    if (defaults.duration !== undefined) {
      const fixedDuration = defaults.duration;
      return z
        .number({ message: messages.durationRequired })
        .refine((v: number) => v === fixedDuration, {
          message: messages.fixedDuration(fixedDuration),
        });
    }

    const minDuration = defaults.minDuration ?? 1;

    return z
      .number({ message: messages.durationRequired })
      .refine((v: number) => Number.isInteger(v), {
        message: messages.durationMustBeWholeDays,
      })
      .min(minDuration, { message: messages.minimumDuration(minDuration) });
  };

  return { getBudgetSchema, getDurationSchema };
}
